import { Button, ButtonTypes } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { MyViewController as Api, type ExportSchedule } from "../../../api/myViewController";
import { Dialog } from "../shared/dialog";
import styles from "../shared/style.module.scss";

const defaults: ExportSchedule = {
	enabled: false,
	frequency: 2,
	hour: 8,
	minute: 0,
	meridiem: 0,
	weekDays: [1],
	weekOfTheMonth: 1,
	dayOfTheMonth: 1,
	monthlyFrequencyType: 1,
};
const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function ExportSettings({
	id,
	board,
	title,
	onClose,
}: {
	id: number;
	board: boolean;
	title: string;
	onClose: () => void;
}) {
	const query = useQuery({
		queryKey: ["myViewV2", "export", board, id],
		queryFn: () => Api.getExportSchedule(id, board),
	});
	const [draft, setDraft] = useState<ExportSchedule>();
	const [offset, setOffset] = useState<number>();
	const savedOffset = offset ?? query.data?.timeZoneOffset ?? new Date().getTimezoneOffset();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const value =
		draft ?? (query.data && [2, 3, 4, 5].includes(query.data.schedule.frequency) ? query.data.schedule : defaults);
	function update(patch: Partial<ExportSchedule>) {
		setDraft({ ...value, ...patch });
	}
	async function save(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError("");
		try {
			await Api.saveExportSchedule(id, board, value, savedOffset);
			await query.refetch();
			onClose();
		} catch {
			setError("Could not save the schedule. Check the selected days and time, then retry.");
		} finally {
			setBusy(false);
		}
	}
	const weekdays = value.frequency === 3 || (value.frequency === 4 && value.monthlyFrequencyType === 2);
	return (
		<Dialog title={`Automated export · ${title}`} onClose={onClose} busy={busy}>
			<form onSubmit={save}>
				<div className={styles.dialogBody}>
					<p>
						Email exports go to your account email, with inline reports and downloadable attachments. Board
						schedules include all saved widgets; individual widget schedules send separately.
					</p>
					{query.data && (
						<p role="status">
							This schedule is shared with old MyView. Changes here apply in both versions.
							{query.data.reactRendererEnabled
								? " You will receive both the legacy and V2 export emails."
								: " Emails currently use the legacy reports."}
						</p>
					)}
					{query.data?.lastSentUtc && (
						<p>Last automated export: {new Date(query.data.lastSentUtc).toLocaleString()}</p>
					)}
					{query.isPending ? (
						<p>Loading schedule…</p>
					) : query.isError ? (
						<p role="alert">
							Could not load the schedule.{" "}
							<Button type="button" onClick={() => query.refetch()}>
								Retry
							</Button>
						</p>
					) : (
						<>
							<label>
								<input
									type="checkbox"
									checked={value.enabled}
									disabled={busy}
									onChange={(e) => update({ enabled: e.target.checked })}
								/>{" "}
								Enable automated export
							</label>
							{value.enabled && (
								<fieldset
									disabled={busy}
									style={{ display: "grid", gap: "1rem", border: 0, padding: 0 }}
								>
									<label>
										Frequency{" "}
										<select
											value={value.frequency}
											onChange={(e) =>
												update({
													frequency: Number(e.target.value),
													weekDays: [value.weekDays?.[0] ?? 1],
													monthlyFrequencyType: 1,
													dayOfTheMonth: 1,
													weekOfTheMonth: 1,
												})
											}
										>
											<option value={2}>Daily</option>
											<option value={3}>Weekly</option>
											<option value={4}>Monthly</option>
											<option value={5}>Quarterly</option>
										</select>
									</label>
									{value.frequency === 4 && (
										<>
											<label>
												Monthly schedule{" "}
												<select
													value={value.monthlyFrequencyType ?? 1}
													onChange={(e) =>
														update({ monthlyFrequencyType: Number(e.target.value) })
													}
												>
													<option value={1}>Day of the month</option>
													<option value={2}>Weekday occurrence</option>
												</select>
											</label>
											{value.monthlyFrequencyType === 1 ? (
												<label>
													Day{" "}
													<input
														type="number"
														min={1}
														max={31}
														required
														value={value.dayOfTheMonth ?? 1}
														onChange={(e) =>
															update({ dayOfTheMonth: Number(e.target.value) })
														}
													/>
												</label>
											) : (
												<label>
													Occurrence{" "}
													<select
														value={value.weekOfTheMonth ?? 1}
														onChange={(e) =>
															update({ weekOfTheMonth: Number(e.target.value) })
														}
													>
														{[
															[1, "First"],
															[2, "Second"],
															[3, "Third"],
															[4, "Fourth"],
															[-1, "Last"],
														].map(([id, name]) => (
															<option key={id} value={id}>
																{name}
															</option>
														))}
													</select>
												</label>
											)}
											<p className={styles.muted}>
												Days 29–31 run only in months containing that day.
											</p>
										</>
									)}
									{weekdays && (
										<fieldset>
											<legend>Days</legend>
											{days.map((day, index) => (
												<label key={day} style={{ display: "block" }}>
													<input
														type={value.frequency === 4 ? "radio" : "checkbox"}
														name="export-weekday"
														checked={value.weekDays?.includes(index) ?? false}
														onChange={(e) =>
															update({
																weekDays:
																	value.frequency === 4
																		? [index]
																		: e.target.checked
																			? [...(value.weekDays ?? []), index]
																			: value.weekDays?.filter(
																					(d) => d !== index,
																				),
															})
														}
													/>{" "}
													{day}
												</label>
											))}
										</fieldset>
									)}
									{value.frequency === 5 ? (
										<p>Runs at noon on the last day of March, June, September, and December.</p>
									) : (
										<label>
											Time{" "}
											<select
												aria-label="Hour"
												value={value.hour}
												onChange={(e) => update({ hour: Number(e.target.value) })}
											>
												{Array.from({ length: 12 }, (_, n) => (
													<option key={n} value={n}>
														{n || 12}
													</option>
												))}
											</select>{" "}
											:{" "}
											<select
												aria-label="Minute"
												value={value.minute}
												onChange={(e) => update({ minute: Number(e.target.value) })}
											>
												{[0, 10, 20, 30, 40, 50].map((n) => (
													<option key={n} value={n}>
														{String(n).padStart(2, "0")}
													</option>
												))}
											</select>{" "}
											<select
												aria-label="AM or PM"
												value={value.meridiem}
												onChange={(e) => update({ meridiem: Number(e.target.value) })}
											>
												<option value={0}>AM</option>
												<option value={1}>PM</option>
											</select>
										</label>
									)}
									<p className={styles.muted}>
										Schedule uses UTC{savedOffset <= 0 ? "+" : "−"}
										{Math.floor(Math.abs(savedOffset) / 60)}:
										{String(Math.abs(savedOffset) % 60).padStart(2, "0")}. Review after daylight
										saving changes.{" "}
										{savedOffset !== new Date().getTimezoneOffset() && (
											<Button
												type="button"
												onClick={() => setOffset(new Date().getTimezoneOffset())}
											>
												Use my current timezone offset
											</Button>
										)}
									</p>
								</fieldset>
							)}
						</>
					)}
					{error && (
						<p className={styles.error} role="alert">
							{error}
						</p>
					)}
				</div>
				<footer>
					<Button type="button" disabled={busy} onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						types={[ButtonTypes.Primary]}
						disabled={
							busy ||
							!query.data ||
							query.isError ||
							(value.enabled && weekdays && !value.weekDays?.length)
						}
					>
						{busy ? "Saving…" : "Save schedule"}
					</Button>
				</footer>
			</form>
		</Dialog>
	);
}
